<template>
  <div class="mb-4">
    <div class="flex text-white">
      <div class="flex w-full items-top mb-3 text-vice-dark-grey gap-2.5 flex-row-reverse">
        <!-- Image Snapshot -->
        <div
          v-if="snapshotData.metadata.mime_type === 'image/jpeg'"
          class="flex-grow rounded-lg p-2 text-sm bg-vice-easy-gray text-vice-dark-gray"
          :class="{ 'flex-grow': snapshotData.thumbnail }"
        >
          <!-- Clinician name, date -->
          <div class="mb-1">
            <span class="font-bold mr-2">{{ snapshotData.device_type.replace(/_/g, ' ') }}</span>
            {{ formatedDate }}
          </div>

          <div class="flex flex-col">
            <div class="flex items-center justify-between">
              <span class="font-medium flex text-vice-dark-grey text-xxs mr-2">
                {{ snapshotData.metadata.width }}x{{ snapshotData.metadata.height }}
              </span>
              <span class="font-semibold flex text-vice-dark-grey text-xxs mr-2">Image</span>
            </div>

            <div class="h-40">
              <div @click="attachmentPreviewOpen = true">
                <img
                  class="w-full rounded-lg overflow-hidden mb-2 block cursor-pointer"
                  :src="snapshotData.url"
                  alt=""
                  v-if="snapshotData.thumbnail"
                />
              </div>

              <button
                class="relative left-2 bottom-10 z-20 text-white"
                title="Remove snapshot"
                @click="
                  $emit('removeSnapshot', { snapshotId: snapshotData.id, type: 'image' });
                  isLoading = true;
                "
              >
                <ViceLoader v-if="isLoading" class="relative ml-1 h-8 w-8" />
                <TrashIcon v-else class="h-6 w-6 shadow-filter" />
              </button>
            </div>

            <a
              class="py-2 flex items-center"
              :href="snapshotData.url"
              v-if="!snapshotData.thumbnail"
              target="_blank"
            >
              <span class="inline-flex"><DocumentDownloadIcon class="w-6 h-6 mr-1" /></span>
              <span class="underline break-all">
                {{ snapshotData.device_type }}
              </span>
            </a>
          </div>
        </div>
        <!-- Video Snapshot -->
        <div
          v-if="snapshotData.metadata.mime_type === 'webm' || snapshotData.status === 'completed'"
          class="flex-grow rounded-lg p-2 text-sm bg-vice-easy-gray text-vice-dark-gray"
          :class="[{ 'flex-grow': true, 'pointer-events-none': snapshotData.file_url === null }]"
        >
          <div class="h-0 relative" @click="attachmentPreviewOpen = true">
            <!-- Play icon -->
            <PlayCircleIcon
              v-if="snapshotData.status === 'completed'"
              class="
                h-16
                w-16
                text-vice-teal-600
                absolute
                right-1/2
                -mr-8
                top-23
                z-20
                cursor-pointer
              "
            />
            <!-- Processing icon -->
            <ViceLoader
              v-if="snapshotData.status === 'processing'"
              colorClass="text-vice-teal-600"
              size="h-14 w-14"
              class="ml-1 text-vice-teal-600 top-31 absolute cursor-not-allowed"
            />
          </div>

          <div class="mb-1">
            <span class="font-bold mr-2">{{ snapshotData.device_type.replace(/_/g, ' ') }}</span>
            {{ formatedDate }}
          </div>

          <div class="flex flex-col">
            <div class="flex items-center justify-between">
              <span class="font-medium flex text-vice-dark-grey text-xxs mr-2">
                {{
                  snapshotData.status === 'completed'
                    ? `${snapshotData.duration_human}`
                    : 'Processing...'
                }}
              </span>
              <span class="font-semibold flex text-vice-dark-grey text-xxs mr-2">
                {{ isAudio ? 'Audio' : 'Video' }}
              </span>
            </div>

            <div class="h-40">
              <div @click="attachmentPreviewOpen = true" class="h-full">
                <!-- Video -->
                <video
                  v-if="!isAudio"
                  class="
                    w-full
                    rounded-lg
                    overflow-hidden
                    mb-2
                    block
                    bg-vice-dark-teal
                    cursor-pointer
                  "
                  :class="{ 'min-h-36': !snapshotData.file_url }"
                >
                  <source type="video/webm" :src="snapshotData.file_url" />
                </video>
                <!-- Audio -->
                <div v-else class="bg-vice-medium-teal h-full rounded-lg cursor-pointer">
                  <ViceStethoscope class="h-14 w-14 rounded-lg cursor-pointer" />
                </div>
              </div>

              <button
                class="relative left-2 bottom-10 z-20 text-white"
                title="Remove snapshot"
                @click="
                  $emit('removeSnapshot', { snapshotId: snapshotData.id, type: 'video' });
                  isLoading = true;
                "
              >
                <ViceLoader v-if="isLoading" class="relative ml-1 h-8 w-8" />
                <TrashIcon v-else class="h-6 w-6 shadow-filter" />
              </button>
            </div>

            <a
              class="py-2 flex items-center"
              :href="snapshotData.file_url"
              v-if="false"
              target="_blank"
            >
              <span class="inline-flex"><DocumentDownloadIcon class="w-6 h-6 mr-1" /></span>
              <span class="underline break-all">
                {{ snapshotData.device_type }}
              </span>
            </a>
          </div>
        </div>
        <!-- Placeholder for recording under processing -->
        <div
          v-if="snapshotData.status === 'processing'"
          class="
            flex-grow
            rounded-lg
            p-2
            text-sm
            bg-vice-easy-gray
            text-vice-dark-gray
            pointer-events-none
          "
        >
          <div class="h-0 relative" @click="attachmentPreviewOpen = true">
            <!-- Processing icon -->
            <ViceLoader
              colorClass="text-vice-teal-600"
              size="h-14 w-14"
              class="ml-1 text-vice-teal-600 top-31 absolute cursor-not-allowed"
            />
          </div>

          <div class="mb-1">
            <span class="font-bold mr-2">{{ snapshotData.device_type.replace(/_/g, ' ') }}</span>
            {{ formatedDate }}
          </div>

          <div class="flex flex-col">
            <div class="flex items-center justify-between">
              <span class="font-medium flex text-vice-dark-grey text-xxs mr-2">Processing...</span>
              <span class="font-semibold flex text-vice-dark-grey text-xxs mr-2">
                {{ isAudio ? 'Audio' : 'Video' }}
              </span>
            </div>

            <div class="h-40">
              <div @click="attachmentPreviewOpen = true" class="h-full">
                <!-- Video -->
                <video
                  v-if="!isAudio"
                  class="
                    w-full
                    rounded-lg
                    overflow-hidden
                    mb-2
                    block
                    bg-vice-dark-teal
                    cursor-pointer
                    min-h-36
                  "
                >
                  <source type="video/webm" :src="null" />
                </video>
              </div>

              <button
                class="relative left-2 bottom-10 z-20 text-white"
                title="Remove snapshot"
                @click="
                  $emit('removeSnapshot', { snapshotId: snapshotData.id, type: 'video' });
                  isLoading = true;
                "
              >
                <ViceLoader v-if="isLoading" class="relative ml-1 h-8 w-8" />
                <TrashIcon v-else class="h-6 w-6 shadow-filter" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <TransitionRoot as="template" :show="attachmentPreviewOpen">
        <Dialog as="div" class="relative z-30" @close="attachmentPreviewOpen = false">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
          </TransitionChild>

          <div class="fixed inset-0 z-10 overflow-y-auto">
            <div
              class="
                flex
                min-h-full
                items-center
                md:items-end
                justify-center
                p-4
                text-center
                sm:items-center
                sm:p-0
              "
            >
              <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enter-to="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leave-from="opacity-100 translate-y-0 sm:scale-100"
                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  class="
                    relative
                    transform
                    overflow-hidden
                    rounded-lg
                    bg-white
                    px-4
                    pt-5
                    pb-4
                    text-left
                    shadow-xl
                    transition-all
                    max-h-screen
                    sm:my-8
                    sm:max-w-7xl
                    sm:p-6
                    min-w-20 min-h-20
                  "
                >
                  <ViceLoader colorClass="text-vice-teal-700" class="left-2 z-10 bottom-0" />

                  <img
                    v-if="snapshotData.metadata.mime_type === 'image/jpeg'"
                    class="w-full max-h-screen rounded relative my-2 z-20 block object-contain"
                    :src="snapshotData.url"
                    alt=""
                  />

                  <video
                    v-if="snapshotData.metadata.mime_type === 'webm'"
                    controls
                    autoplay
                    class="
                      w-full
                      max-h-screen
                      rounded
                      relative
                      overflow-hidden
                      my-2
                      block
                      object-contain
                      zoom-125
                      z-20
                    "
                    :class="{ 'audio-class': isAudio }"
                    :src="snapshotData.file_url"
                  />
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import dayjs from 'dayjs';
import { DocumentDownloadIcon, TrashIcon } from '@heroicons/vue/solid';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { VICE_SESSION_PHASES } from '@/Utils/vice-constants';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import PlayCircleIcon from '@/Components/Icons/PlayCircleIcon.vue';
import ViceStethoscope from '@/Components/Icons/ViceStethoscope.vue';

export default {
  components: {
    ViceStethoscope,
    PlayCircleIcon,
    ViceLoader,
    TrashIcon,
    Dialog,
    DialogPanel,
    TransitionRoot,
    TransitionChild,
    DocumentDownloadIcon,
  },

  props: {
    snapshotData: {
      required: true,
    },
    clinicianName: {
      type: String,
    },
  },
  data: () => ({
    systemMessages: {
      introduction: 1,
      consultation: 2,
      debrief: 3,
    },
    attachmentPreviewOpen: false,
  }),
  setup() {
    const isLoading = ref(false);
    return { isLoading, VICE_SESSION_PHASES };
  },
  computed: {
    formatedDate() {
      return dayjs(this.snapshotData.captured_at).format('HH:mm') ?? '';
    },
    isAudio() {
      return this.snapshotData.device_type === 'Stethoscope';
    },
  },
};
</script>

<style>
.right-26 {
  right: 6.5rem;
}
.audio-class {
  height: 40px;
  min-width: 34rem;
}
</style>
